import CobraSpinner from '../shared/CobraSpinner';
import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    SimpleGrid,
    VStack,
    HStack,
    Icon,
    useToast,
    Badge,
    Flex,
    Button,
    Divider
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FaIcons from 'react-icons/fa';
import { FaGraduationCap, FaArrowLeft, FaBookOpen, FaLayerGroup } from 'react-icons/fa';
import niveauService from '../services/niveauService';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const MotionBox = motion(Box);

const CourseDiscovery = () => {
    const [niveaux, setNiveaux] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const toast = useToast();

    // Theme Constants
    const brandYellow = '#F2C919';
    const lightBg = 'gray.50';
    const primaryText = '#2C3E50';

    useEffect(() => {
        fetchNiveaux();
    }, []);

    const fetchNiveaux = async () => {
        try {
            const response = await niveauService.getNiveaux();
            setNiveaux(response.data);
        } catch (error) {
            toast({
                title: 'خطأ في تحميل المستويات',
                description: 'تعذر الاتصال بالخادم، يرجى المحاولة لاحقاً.',
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top'
            });
        } finally {
            setLoading(false);
        }
    };

    const getIconComponent = (iconName) => {
        return FaIcons[iconName] || FaGraduationCap;
    };

    if (loading) {
        return (
            <Flex h="100vh" align="center" justify="center" dir="rtl" bg={lightBg}>
                <VStack spacing={6}>
                    <CobraSpinner size="xl" color={brandYellow} thickness="4px" />
                    <Text color={primaryText} fontFamily="Tajawal, sans-serif" fontWeight="700" fontSize="lg">
                        جاري تهيئة المستويات...
                    </Text>
                </VStack>
            </Flex>
        );
    }

    return (
        <Box dir="rtl" bg={lightBg} minH="100vh" color={primaryText} overflowX="hidden">
            <Header />

            <Box pt={{ base: 32, md: 40 }} pb={{ base: 16, md: 24 }} position="relative" zIndex={1}>
                <Container maxW="1400px" textAlign="center">
                    <MotionBox
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <VStack spacing={6}>
                            <Badge
                                bg={`${brandYellow}20`}
                                color={primaryText}
                                px={6}
                                py={2}
                                borderRadius="full"
                                fontSize="md"
                                fontFamily="Tajawal, sans-serif"
                                fontWeight="800"
                            >
                                اكتشف منصتنا
                            </Badge>
                            <Heading 
                                fontSize={{ base: '5xl', md: '7xl' }} 
                                fontFamily="Tajawal, sans-serif" 
                                fontWeight="900"
                                lineHeight="1.2"
                                color={primaryText}
                            >
                                قائمة المستويات الدراسية
                            </Heading>
                            <Text fontSize={{ base: 'lg', md: '2xl' }} color="gray.600" maxW="3xl" fontFamily="Tajawal, sans-serif" fontWeight="600">
                                اضغط على مستواك الدراسي لاستكشاف المواد والدروس المتاحة والبدء في رحلة التعلم.
                            </Text>
                        </VStack>
                    </MotionBox>
                </Container>
            </Box>

            <Container maxW="1400px" py={10} position="relative" zIndex={1}>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={10}>
                    {niveaux.map((niveau, index) => {
                        const IconComponent = getIconComponent(niveau.icon);
                        const niveauColor = niveau.color || brandYellow;

                        return (
                            <MotionBox
                                key={niveau._id}
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1, type: "spring", bounce: 0.4 }}
                                whileHover={{ y: -10, scale: 1.02 }}
                                onClick={() => navigate(`/niveau/${niveau._id}/packs`)}
                                cursor="pointer"
                                h="100%"
                            >
                                <Box
                                    bg="white"
                                    borderRadius="3xl"
                                    overflow="hidden"
                                    border="1px solid"
                                    borderColor="gray.100"
                                    transition="all 0.4s"
                                    _hover={{ 
                                        borderColor: `${niveauColor}50`, 
                                        boxShadow: `0 20px 40px -10px ${niveauColor}30` 
                                    }}
                                    h="100%"
                                    display="flex"
                                    flexDirection="column"
                                    position="relative"
                                    boxShadow="0 10px 30px rgba(0,0,0,0.05)"
                                >
                                    <VStack p={10} spacing={6} align="stretch" flex={1}>
                                        <VStack spacing={4} align="center">
                                            <Box
                                                p={6}
                                                bg={`${niveauColor}15`}
                                                borderRadius="2xl"
                                                color={niveauColor}
                                            >
                                                <Icon as={IconComponent} boxSize={10} />
                                            </Box>
                                            <VStack spacing={3}>
                                                <Heading size="xl" color={primaryText} fontFamily="Tajawal, sans-serif" fontWeight="900" textAlign="center">
                                                    {niveau.name}
                                                </Heading>
                                                <Badge
                                                    bg={`${niveauColor}15`}
                                                    color={niveauColor}
                                                    px={4}
                                                    py={1}
                                                    borderRadius="full"
                                                    fontSize="sm"
                                                    fontWeight="800"
                                                    fontFamily="Tajawal, sans-serif"
                                                >
                                                    {niveau.matieres?.length || 0} مادة تعليمية
                                                </Badge>
                                            </VStack>
                                        </VStack>

                                        <Divider borderColor="gray.100" my={2} />

                                        <VStack align="flex-start" spacing={4} flex={1}>
                                            <HStack spacing={2}>
                                                <Icon as={FaBookOpen} color={niveauColor} />
                                                <Text fontWeight="800" fontFamily="Tajawal, sans-serif" color="gray.700" fontSize="sm">
                                                    المواد الدراسية:
                                                </Text>
                                            </HStack>
                                            <VStack align="stretch" w="100%" spacing={3}>
                                                {niveau.matieres?.map((matiere, idx) => (
                                                    <HStack key={idx} p={3} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.100">
                                                        <Icon as={FaLayerGroup} color="gray.500" boxSize={3} />
                                                        <Text fontSize="sm" fontFamily="Tajawal, sans-serif" fontWeight="700" color="gray.700">
                                                            {matiere.name}
                                                        </Text>
                                                    </HStack>
                                                ))}
                                            </VStack>
                                        </VStack>

                                        <Button
                                            mt={6}
                                            w="100%"
                                            h="60px"
                                            bg="gray.50"
                                            color={primaryText}
                                            _hover={{ bg: niveauColor, color: 'white' }}
                                            borderRadius="2xl"
                                            fontFamily="Tajawal, sans-serif"
                                            fontWeight="900"
                                            rightIcon={<Icon as={FaArrowLeft} mr={2} boxSize={4} />}
                                            transition="all 0.3s"
                                        >
                                            استكشف المواد
                                        </Button>
                                    </VStack>
                                </Box>
                            </MotionBox>
                        );
                    })}
                </SimpleGrid>

                {niveaux.length === 0 && (
                    <Flex direction="column" align="center" justify="center" py={32} bg="white" borderRadius="3xl" border="1px dashed" borderColor="gray.200">
                        <Icon as={FaGraduationCap} boxSize={20} color="gray.300" mb={6} />
                        <Heading size="md" color="gray.500" fontFamily="Tajawal, sans-serif">لا توجد محتويات متاحة حالياً</Heading>
                    </Flex>
                )}
            </Container>

            <Footer />
        </Box>
    );
};

export default CourseDiscovery;
